import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

/** Conclusão da produção: unidades boas, refugo e filamento adicional consumido. */
export function FinishProductionModal({ production: p, onClose, onSaved }) {
  const planned = Number(p.quantity_produced || 0);
  const [good, setGood] = useState(String(planned));
  const [extra, setExtra] = useState({});
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const goodNum = Math.max(0, Math.min(planned, Number(good || 0)));
  const lost = planned - goodNum;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/productions/${p.id}/finish`, {
        good_quantity: goodNum,
        extra_filaments: Object.entries(extra)
          .filter(([, g]) => Number(g) > 0)
          .map(([filament_id, g]) => ({ filament_id, quantity_g: Number(g) })),
        failure_reason: reason,
        notes,
      });
      toast.success(
        goodNum ? `Produção concluída: ${goodNum} un boas.` : "Produção registrada como falha."
      );
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Concluir produção"
      description={`${p.product_name} · ${planned} un planejadas`}
      testid="finish-production-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Quantidade boa">
            <Input
              type="number"
              min="0"
              max={planned}
              value={good}
              onChange={(e) => setGood(e.target.value)}
              className="font-num"
              data-testid="finish-good-input"
            />
          </Field>
          <Field label="Refugo (perdidas)">
            <Input value={`${lost} un`} readOnly className="font-num" data-testid="finish-lost-value" />
          </Field>
        </div>

        {p.kind !== "MONTAGEM" && p.consumption_preview?.length > 0 && (
          <div className="space-y-3 rounded-lg border border-white/10 bg-secondary/40 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Consumo previsto e refugo adicional
            </p>
            {p.consumption_preview.map((c) => (
              <div key={c.filament_id} className="grid grid-cols-[1fr_auto] items-center gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm">{c.label}</p>
                  <p className="font-num text-[11px] text-muted-foreground">
                    previsto {c.needed_g} g · disponível {c.available_g} g
                  </p>
                </div>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="+ g"
                  value={extra[c.filament_id] ?? ""}
                  onChange={(e) =>
                    setExtra((s) => ({ ...s, [c.filament_id]: e.target.value }))
                  }
                  className="h-9 w-24 font-num"
                  data-testid={`finish-extra-${c.filament_id}`}
                />
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">
              O adicional é registrado separadamente como refugo, sem se misturar ao consumo
              previsto.
            </p>
          </div>
        )}

        {lost > 0 && (
          <Field label="Motivo da falha">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: descolou da mesa"
              data-testid="finish-reason-input"
            />
          </Field>
        )}
        <Field label="Observações">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            data-testid="finish-notes-input"
          />
        </Field>

        <div className="flex justify-end gap-2 border-t border-white/10 pt-4">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full" data-testid="finish-cancel-btn">
            Cancelar
          </Button>
          <Button type="submit" disabled={busy} className="rounded-full" data-testid="finish-submit-btn">
            {busy ? "Salvando..." : "Confirmar conclusão"}
          </Button>
        </div>
      </form>
    </ResponsiveModal>
  );
}
